import type { RouteObject } from "react-router-dom";
import NotFound from "@/pages/NotFound";
import Home from "@/pages/home/page";
import LoginPage from "@/pages/login/page";
import DashboardPage from "@/pages/dashboard/page";
import ContactsPage from "@/pages/contacts/page";
import ImportTutorialPage from "@/pages/contacts/import-tutorial/page";
import GroupsPage from "@/pages/groups/page";
import TemplatesPage from "@/pages/templates/page";
import CampaignsPage from "@/pages/campaigns/page";
import CampaignProgressPage from "@/pages/campaigns/progress/page";
import AdminLayout from "@/components/feature/AdminLayout";
import { AuthGuard, GuestGuard } from "@/components/feature/AuthGuard";
import PlanGuard from "@/components/feature/PlanGuard";
import SettingsPage from "@/pages/settings/page";
import SettingsWhatsAppPage from "@/pages/settings/whatsapp/page";
import SettingsGeneralPage from "@/pages/settings/general/page";
import SettingsUsersPage from "@/pages/settings/users/page";
import SettingsSmtpPage from "@/pages/settings/smtp/page";
import SettingsMercadoPagoPage from "@/pages/settings/mercadopago/page";
import SettingsStripePage from "@/pages/settings/stripe/page";
import SettingsGoogleOAuthPage from "@/pages/settings/google-oauth/page";
import SettingsEvolutionApiPage from "@/pages/settings/evolution-api/page";
import SettingsMetaApiPage from "@/pages/settings/meta-api/page";
import SettingsInfrastructurePage from "@/pages/settings/infrastructure/page";
import SettingsBackupPage from "@/pages/settings/backup/page";
import SettingsMonitoringPage from "@/pages/settings/monitoring/page";
import SettingsUpdatesPage from "@/pages/settings/updates/page";
import SettingsAntiSpamPage from "@/pages/settings/anti-spam/page";
import ReportsPage from "@/pages/reports/page";
import LogsPage from "@/pages/logs/page";
import ProfilePage from "@/pages/profile/page";
import SubscriptionPage from "@/pages/subscription/page";
import OnboardingPage from "@/pages/onboarding/page";
import CreditLandingPage from "@/pages/credit-landing/page";
import SetupFeePage from "@/pages/setup-fee/page";
import MyUsagePage from "@/pages/my-usage/page";
import InvoicesPage from "@/pages/invoices/page";
import AdminPlansPage from "@/pages/admin/plans/page";
import AdminUsersPage from "@/pages/admin/users/page";
import AdminDashboardPage from "@/pages/admin/dashboard/page";
import AdminLogsPage from "@/pages/admin/logs/page";
import DiagnosticPage from "@/pages/diagnostic/page";
import DeployPage from "@/pages/deploy/page";
import SecurityPage from "@/pages/security/page";
import ForgotPasswordPage from "@/pages/forgot-password/page";
import ResetPasswordPage from "@/pages/reset-password/page";
import AdminRouteGuard from "@/components/feature/AdminRouteGuard";
import IntegrationsPage from "@/pages/integrations/page";
import NotificationsPage from "@/pages/notifications/page";
import AdminInvoicesPage from "@/pages/admin/invoices/page";
import AdminErrorsPage from "@/pages/admin/errors/page";
import TutorialWhatsAppPage from "@/pages/tutorial/whatsapp/page";
import PrivacyPage from "@/pages/privacy/page";
import TermsPage from "@/pages/terms/page";
import FaqPage from "@/pages/faq/page";
import AboutPage from "@/pages/about/page";
import ContactPage from "@/pages/contact/page";
import BlogPostPage from "@/pages/blog/detail/page";
import SupportTicketsPage from "@/pages/support/page";
import SupportTicketDetailPage from "@/pages/support/detail/page";
import SupportNewTicketPage from "@/pages/support/new/page";
import GoogleAuthCallbackPage from "@/pages/auth/google-callback/page";

const routes: RouteObject[] = [
  {
    path: "/",
    element: <Home />,
  },
  {
    path: "/auth/google-callback",
    element: <GoogleAuthCallbackPage />,
  },
  {
    path: "/privacy",
    element: <PrivacyPage />,
  },
  {
    path: "/terms",
    element: <TermsPage />,
  },
  {
    path: "/faq",
    element: <FaqPage />,
  },
  {
    path: "/about",
    element: <AboutPage />,
  },
  {
    path: "/contact",
    element: <ContactPage />,
  },
  {
    path: "/blog/:slug",
    element: <BlogPostPage />,
  },
  {
    path: "/tutorial/whatsapp",
    element: <TutorialWhatsAppPage />,
  },
  {
    path: "/creditos",
    element: <CreditLandingPage />,
  },
  {
    element: <GuestGuard />,
    children: [
      {
        path: "/login",
        element: <LoginPage />,
      },
      {
        path: "/forgot-password",
        element: <ForgotPasswordPage />,
      },
      {
        path: "/reset-password",
        element: <ResetPasswordPage />,
      },
    ],
  },
  {
    element: <AuthGuard />,
    children: [
      {
        path: "/onboarding",
        element: <OnboardingPage />,
      },
      {
        element: <AdminLayout />,
        children: [
          {
            path: "/dashboard",
            element: <DashboardPage />,
          },
          {
            path: "/contacts",
            element: <PlanGuard feature="contacts"><ContactsPage /></PlanGuard>,
          },
          {
            path: "/contacts/import-tutorial",
            element: <PlanGuard feature="contacts"><ImportTutorialPage /></PlanGuard>,
          },
          {
            path: "/groups",
            element: <PlanGuard feature="groups"><GroupsPage /></PlanGuard>,
          },
          {
            path: "/templates",
            element: <PlanGuard feature="templates"><TemplatesPage /></PlanGuard>,
          },
          {
            path: "/campaigns",
            element: <PlanGuard feature="campaigns"><CampaignsPage /></PlanGuard>,
          },
          {
            path: "/campaigns/:id/progress",
            element: <PlanGuard feature="campaigns"><CampaignProgressPage /></PlanGuard>,
          },
          {
            path: "/reports",
            element: <PlanGuard feature="reports"><ReportsPage /></PlanGuard>,
          },
          {
            path: "/settings",
            element: <PlanGuard feature="settings_general"><SettingsPage /></PlanGuard>,
            children: [
              {
                path: "whatsapp",
                element: <PlanGuard feature="settings_whatsapp"><SettingsWhatsAppPage /></PlanGuard>,
              },
              {
                path: "anti-spam",
                element: <PlanGuard feature="settings_whatsapp"><SettingsAntiSpamPage /></PlanGuard>,
              },
              {
                path: "general",
                element: <PlanGuard feature="settings_general"><SettingsGeneralPage /></PlanGuard>,
              },
              {
                path: "users",
                element: <PlanGuard feature="settings_users"><SettingsUsersPage /></PlanGuard>,
              },
              // Admin-only settings below
              {
                path: "smtp",
                element: <AdminRouteGuard><SettingsSmtpPage /></AdminRouteGuard>,
              },
              {
                path: "mercadopago",
                element: <AdminRouteGuard><SettingsMercadoPagoPage /></AdminRouteGuard>,
              },
              {
                path: "stripe",
                element: <AdminRouteGuard><SettingsStripePage /></AdminRouteGuard>,
              },
              {
                path: "google-oauth",
                element: <AdminRouteGuard><SettingsGoogleOAuthPage /></AdminRouteGuard>,
              },
              {
                path: "evolution-api",
                element: <AdminRouteGuard><SettingsEvolutionApiPage /></AdminRouteGuard>,
              },
              {
                path: "meta-api",
                element: <AdminRouteGuard><SettingsMetaApiPage /></AdminRouteGuard>,
              },
              {
                path: "infrastructure",
                element: <AdminRouteGuard><SettingsInfrastructurePage /></AdminRouteGuard>,
              },
              {
                path: "backup",
                element: <AdminRouteGuard><SettingsBackupPage /></AdminRouteGuard>,
              },
              {
                path: "monitoring",
                element: <AdminRouteGuard><SettingsMonitoringPage /></AdminRouteGuard>,
              },
              {
                path: "updates",
                element: <AdminRouteGuard><SettingsUpdatesPage /></AdminRouteGuard>,
              },
            ],
          },
          {
            path: "/logs",
            element: <PlanGuard feature="logs"><LogsPage /></PlanGuard>,
          },
          {
            path: "/profile",
            element: <ProfilePage />,
          },
          {
            path: "/subscription",
            element: <SubscriptionPage />,
          },
          {
            path: "/setup-fee",
            element: <PlanGuard feature="setup_fee"><SetupFeePage /></PlanGuard>,
          },
          {
            path: "/my-usage",
            element: <PlanGuard feature="my_usage"><MyUsagePage /></PlanGuard>,
          },
          {
            path: "/diagnostic",
            element: <AdminRouteGuard><DiagnosticPage /></AdminRouteGuard>,
          },
          {
            path: "/deploy",
            element: <AdminRouteGuard><PlanGuard feature="deploy"><DeployPage /></PlanGuard></AdminRouteGuard>,
          },
          {
            path: "/security",
            element: <AdminRouteGuard><SecurityPage /></AdminRouteGuard>,
          },
          {
            path: "/admin/plans",
            element: <AdminRouteGuard><AdminPlansPage /></AdminRouteGuard>,
          },
          {
            path: "/admin/users",
            element: <AdminRouteGuard><AdminUsersPage /></AdminRouteGuard>,
          },
          {
            path: "/admin/dashboard",
            element: <AdminRouteGuard><AdminDashboardPage /></AdminRouteGuard>,
          },
          {
            path: "/admin/logs",
            element: <AdminRouteGuard><AdminLogsPage /></AdminRouteGuard>,
          },
          {
            path: "/integrations",
            element: <IntegrationsPage />,
          },
          {
            path: "/invoices",
            element: <InvoicesPage />,
          },
          {
            path: "/notifications",
            element: <NotificationsPage />,
          },
          {
            path: "/admin/invoices",
            element: <AdminRouteGuard><AdminInvoicesPage /></AdminRouteGuard>,
          },
          {
            path: "/support",
            element: <SupportTicketsPage />,
          },
          {
            path: "/support/new",
            element: <SupportNewTicketPage />,
          },
          {
            path: "/support/:id",
            element: <SupportTicketDetailPage />,
          },
          {
            path: "/admin/errors",
            element: <AdminRouteGuard><AdminErrorsPage /></AdminRouteGuard>,
          },
        ],
      },
    ],
  },
  {
    path: "*",
    element: <NotFound />,
  },
];

export default routes;